import { useMutation } from '@apollo/client';
import Box from '@risk-smart/themed-cloudscape-components/box';
import FormField from '@risk-smart/themed-cloudscape-components/form-field';
import Modal from '@risk-smart/themed-cloudscape-components/modal';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import {
  Appetite_Model_Enum,
  Recalculate_Appetites_Error_Code_Enum,
  RecalculateAppetitesDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledJsonEditor } from 'src/components/form/controlled-json-editor/ControlledJsonEditor';
import ControlledSelect from 'src/components/form/controlled-select';
import { useModulesStore } from 'src/context/moduleContext';
import { useAggregation } from 'src/hooks/useAggregation';
import { useIsCustomerSupport } from 'src/hooks/useIsCustomerSupport';

export const AppetiteCascadingSettings: FC = () => {
  const {
    control,
    formState: { isDirty: isFormDirty },
  } = useFormContext();
  const { t } = useTranslation('common', { keyPrefix: 'modules.fields' });
  const { t: ct } = useTranslation('common');
  const isCustomerSupport = useIsCustomerSupport();
  const { addNotification } = useNotifications();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Read directly from aggregation_org (same source the lambda checks) so the gate can't drift from persisted state.
  // Gate on both store-level dirtiness (module toggles via useModulesStore.toggle) and RHF form dirtiness (this select / json editor).
  const { appetiteAggregation, loading: aggregationLoading } = useAggregation();
  const isStoreDirty = useModulesStore((state) => state.isDirty);
  const cascadeEnabled =
    !isStoreDirty &&
    !isFormDirty &&
    !aggregationLoading &&
    appetiteAggregation === Appetite_Model_Enum.TopDownCascade;

  const [recalculateAppetites, { loading: recalculating }] = useMutation(
    RecalculateAppetitesDocument
  );

  const handleRecalculate = async () => {
    setShowConfirmModal(false);
    try {
      const result = await recalculateAppetites();
      const errorCode = result.data?.recalculateAppetites?.errorCode ?? null;
      if (
        errorCode === Recalculate_Appetites_Error_Code_Enum.CascadeNotConfigured
      ) {
        addNotification({
          type: 'error',
          content: t('RecalculateAppetiteCascade_error_cascade_not_configured'),
        });

        return;
      }
      if (
        errorCode === Recalculate_Appetites_Error_Code_Enum.CascadeNotEnabled
      ) {
        addNotification({
          type: 'error',
          content: t('RecalculateAppetiteCascade_error_cascade_not_enabled'),
        });

        return;
      }
      addNotification({
        type: 'success',
        content: t('RecalculateAppetiteCascade_success'),
      });
    } catch {
      addNotification({
        type: 'error',
        content: t('RecalculateAppetiteCascade_error'),
      });
    }
  };

  return (
    <SpaceBetween size={'s'}>
      <FormField>
        <ControlledSelect
          testId={'appetiteCascadingModel'}
          control={control}
          name={'AppetiteCascadingModel'}
          label={t('AppetiteCascadingModel')}
          options={[
            {
              label: t('AppetiteCascadingModels.default'),
              value: Appetite_Model_Enum.Default,
            },
            {
              label: t('AppetiteCascadingModels.top_down_cascade'),
              value: Appetite_Model_Enum.TopDownCascade,
            },
          ]}
        />
      </FormField>
      <FormField>
        <ControlledJsonEditor
          control={control}
          name={'AppetiteCascadingModelConfig'}
          label={t('AppetiteCascadingModelConfig')}
        />
      </FormField>
      {isCustomerSupport && cascadeEnabled && (
        <>
          <Button
            data-testid={'recalculate-appetite-cascade-button'}
            variant={'normal'}
            onClick={() => setShowConfirmModal(true)}
            loading={recalculating}
            disabled={recalculating}
          >
            {t('RecalculateAppetiteCascade')}
          </Button>
          <Modal
            data-testid={'recalculate-appetite-cascade-modal'}
            visible={showConfirmModal}
            onDismiss={() => setShowConfirmModal(false)}
            header={t('RecalculateAppetiteCascade_confirm_header')}
            closeAriaLabel={ct('closeModal')}
            size={'small'}
            footer={
              <Box float={'right'}>
                <SpaceBetween direction={'horizontal'} size={'xs'}>
                  <Button
                    variant={'normal'}
                    onClick={() => setShowConfirmModal(false)}
                  >
                    {ct('cancel')}
                  </Button>
                  <Button variant={'primary'} onClick={handleRecalculate}>
                    {t('RecalculateAppetiteCascade_confirm_button')}
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            {t('RecalculateAppetiteCascade_confirm_body')}
          </Modal>
        </>
      )}
    </SpaceBetween>
  );
};
